import {
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MultiSelect } from 'primeng/multiselect';
import { MxBaseComponent } from 'src/app/core/base/mx.base-component';

@Component({ standalone: false,
  selector: 'mx-multi-select',
  templateUrl: './mx.multi-select.component.html',
  styleUrls: ['./mx.multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MxMultiSelectComponent),
      multi: true,
    },
  ],
})
export class MxMultiSelectComponent
  extends MxBaseComponent
  implements ControlValueAccessor
{
  @ViewChild('multiselect') multiSelect: MultiSelect;

  /**
   * Identifier of the input element.
   */
  @HostBinding('attr.id')
  externalId = '';

  @Input()
  set id(value: string) {
    this._id = value;
    this.externalId = null;
  }

  get id() {
    return this._id;
  }

  private _id = '';

  /**
   * Name of the input element.
   */
  @HostBinding('attr.name')
  externalName = '';

  @Input()
  set name(value: string) {
    this._name = value;
    this.externalName = null;
  }

  get inputName() {
    return this._name;
  }

  private _name = '';

  /**
   * Index of the element in tabbing order.
   */
  @HostBinding('attr.tabindex')
  externalTabindex = '';

  @Input()
  set tabindex(value: number) {
    this._tabindex = value;
    this.externalTabindex = null;
  }

  get tabindex() {
    return this._tabindex;
  }

  private _tabindex: number = null;

  /**
   * Value of the input element.
   */
  @Input('value') _value: any = null;
  onChange: any = () => {};
  onTouched: any = () => {};

  get value() {
    return this._value;
  }

  set value(val) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  /**
   * Component is visible
   */
  @Input() visible: boolean = true;

  /**
   * The value is object
   */
  @Input() isObject: boolean = false;

  /**
   * Target element to attach the overlay, valid values are "body" or a local ng-template variable of another element (note: use binding with brackets for template variables, e.g. [appendTo]="mydiv" for a div element having #mydiv as variable name).
   */
  @Input() appendTo: string = 'body';

  /**
   * 	An array of objects to display as the available options.
   */
  @Input() autofocusFilter: boolean = false;

  /**
   * 	Whether to automatically manage layering.
   */
  @Input() autoZIndex: boolean = false;

  /**
   * 	Base zIndex value to use in layering.
   */
  @Input() baseZIndex: number = 0;

  /**
   * A property to uniquely identify a value in options.
   */
  @Input() dataKey: string;

  /**
   * When present, it specifies that the component should be disabled.
   */
  @Input() disabled: boolean = false;

  /**
   * Whether to show labels of selected item labels or use default label.
   */
  @Input() displaySelectedLabel: boolean = true;

  /**
   * Icon class of the dropdown icon.
   */
  @Input() dropdownIcon: string = 'fa fa-chevron-down fa-fw';

  /**
   * Text to display when filtering does not return any results.
   */
  @Input() emptyFilterMessage: string = 'No_records_found';

  /**
   * When specified, displays an input field to filter the items on keyup.
   */
  @Input() filter: boolean = true;

  /**
   * Defines how the items are filtered, valid values are "contains" (default) "startsWith", "endsWith", "equals", "notEquals", "in", "lt", "lte", "gt" and "gte".
   */
  @Input() filterMatchMode: 'contains' | 'startsWith' | 'endsWith' | 'equals' | 'notEquals' | 'in' | 'lt' | 'lte' | 'gt' | 'gte' = 'contains';

  /**
   * 	When specified, filter displays with this value.
   */
  @Input() filterValue: string;

  /**
   * When filtering is enabled, filterBy decides which field or fields (comma separated) to search against.
   */
  @Input() filterBy: string = 'label';

  /**
   * 	Defines placeholder of the filter input.
   */
  @Input() filterPlaceHolder: string;

  /**
   * 	Height of an item in the list for VirtualScrolling.
   */
  @Input() itemSize: number = null;

  /**
   * 	Decides how many selected item labels to show at most.
   */
  @Input() maxSelectedLabels: number = 3;

  /**
   * 	An array of objects to display as the available options.
   */
  @Input() options: any;

  /**
   * 	Name of the label field of an option.
   */
  @Input() optionLabel: string = 'label';

  /**
   * Name of the value field of an option.
   */
  @Input() optionValue: string = 'value';

  /**
   * Name of the disabled field of an option.
   */
  @Input() optionDisabled: string = 'disabled';

  /**
   * Use selected item template
   */
  @Input() overlayVisible: boolean = false;

  /**
   * Inline style of the overlay panel.
   */
  @Input() panelStyle: object;

  /**
   * Default text to display when no option is selected.
   */
  @Input() placeholder: string = 'Select_please';

  /**
   * When present, it specifies that the component cannot be edited.
   */
  @Input() readonly: boolean = false;

  /**
   * Text to display when there is no data.
   */
  @Input() emptyMessage: string = 'No_records_found';

  /**
   * Clears the filter value when hiding the dropdown.
   */
  @Input() resetFilterOnHide: boolean = false;

  /**
   * Height of the viewport in pixels, a scrollbar is defined if height of list exceeds this value.
   */
  @Input() scrollHeight: string = '200px';

  /**
   * Label to display after exceeding max selected labels e.g. ({0} items selected), defaults "ellipsis" keyword to indicate a text-overflow.
   */
  @Input() selectedItemsLabel: string = 'ellipsis';

  /**
   * Number of maximum options that can be selected.
   */
  @Input() selectionLimit: number;

  /**
   * 	Whether to show the header.
   */
  @Input() showHeader: boolean = true;

  /**
   * 	Whether to show the checkbox at header to toggle all items at once.
   */
  @Input() showToggleAll: boolean = true;

  /**
   * Inline style of the element.
   */
  @Input() style: object;

  /**
   * Style class of the element.
   */
  @Input() styleClass: any;

  /**
   * Whether the data should be loaded on demand during scroll.
   */
  @Input() virtualScroll: boolean;

  /**
   * Use selected item template
   */
  @Input() useSelectedItemsTemplate: boolean;

  /**
   * Use item template
   */
  @Input() useItemTemplate: boolean;

  @ContentChild('itemTemplateRef', { static: false })
  itemTemplateRef: TemplateRef<any>;
  @ContentChild('selectedItemsTemplateRef', { static: false })
  selectedItemsTemplateRef: TemplateRef<any>;

  /**
   * Callback to invoke when component is clicked.
   */
  @Output() mxOnClick: EventEmitter<any> = new EventEmitter<any>();

  /**
   * 	Callback to invoke when value of dropdown changes.
   */
  @Output() mxOnChange: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when data is filtered.
   */
  @Output() mxOnFilter: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown gets focus.
   */
  @Output() mxOnFocus: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown loses focus.
   */
  @Output() mxOnBlur: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown overlay gets visible.
   */
  @Output() mxOnPanelShow: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown overlay gets hidden.
   */
  @Output() mxOnPanelHide: EventEmitter<any> = new EventEmitter<any>();

  constructor() {
    super();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  writeValue(value: any) {
    this.value = value || null;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  mxClick(event: any) {
    this.mxOnClick.emit(event);
  }

  mxChange(event: any) {
    this.mxOnChange.emit(event);
  }

  mxFilter(event: any) {
    this.mxOnFilter.emit(event);
  }

  mxFocus(event: any) {
    this.mxOnFocus.emit(event);
  }

  mxBlur(event: any) {
    this.mxOnBlur.emit(event);
  }

  mxPanelShow(event: any) {
    this.mxOnPanelShow.emit(event);
  }

  mxPanelHide(event: any) {
    this.mxOnPanelHide.emit(event);
  }

  /* Hides the panel. */
  show() {
    this.multiSelect.show();
  }

  /* Hides the panel. */
  hide() {
    this.multiSelect.hide();
  }
}
